// src/pages/Advertisement/Admin/ResetPasswordAds.tsx

import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { setAccessToken } from "../../../shared/api/axios";
import { useAuth } from "../../../shared/contexts/AuthContext";
import HighLogo from "../../HighLogo";
import logo from "../../../assets/logo.png";
import StartFooter from "../StartFooterAd";

const ResetPasswordAds = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setToken, setUser } = useAuth();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordChecks = useMemo(
    () => ({
      length: newPassword.length >= 8,
      digit: /\d/.test(newPassword),
    }),
    [newPassword]
  );

  const passwordValid =
    passwordChecks.length &&
    passwordChecks.digit;

  const passwordsMatch =
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  const canSubmit =
    Boolean(token) &&
    passwordValid &&
    passwordsMatch &&
    !loading;

  const handleResetPassword = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!token) {
      setError(
        t("resetPassword.invalidToken", {
          defaultValue:
            "The password reset link is invalid or incomplete.",
        })
      );
      return;
    }

    if (!passwordValid || !passwordsMatch) {
      setError(
        t("resetPassword.requirements", {
          defaultValue:
            "Please meet the password requirements and make sure both fields match.",
        })
      );
      return;
    }

    setLoading(true);

    try {
      await api.post(`/auth/reset-password/${token}`, {
        newPassword,
      });

      setAccessToken(null);
      setToken(null);
      setUser(null);

      localStorage.removeItem("unyToken");
      localStorage.removeItem("unyProfile");

      sessionStorage.removeItem("passwordResetFlow");
      sessionStorage.removeItem("passwordResetReturnTo");

      setSuccess(
        t("resetPassword.success", {
          defaultValue:
            "Your password has been reset successfully.",
        })
      );

      setNewPassword("");
      setConfirmPassword("");

      window.setTimeout(() => {
        navigate("/ad-login", { replace: true });
      }, 1200);
    } catch (err: any) {
      console.error("Advertiser password reset error:", err);

      const message =
        err?.response?.data?.msg ||
        err?.message ||
        t("resetPassword.resetError", {
          defaultValue:
            "The password could not be reset. The link may have expired.",
        });

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={floatingLogoStyle}>
        <button
          type="button"
          onClick={() => navigate("/ads-home")}
          aria-label="Go to ELNYDO Ads homepage"
          style={floatingLogoButtonStyle}
        >
          <HighLogo
            src={logo}
            style={{
              width: 190,
              display: "block",
            }}
            padding={0}
          />
        </button>
      </div>

      <main style={mainStyle}>
        <section style={resetPanel}>
          <button
            type="button"
            onClick={() => navigate("/ad-login")}
            aria-label="Back to advertiser login"
            style={backButton}
          >
            ‹
          </button>

          <h1 style={titleStyle}>
            <span style={{ color: "#f8fafc" }}>
              {t("resetPassword.title", {
                defaultValue: "Reset Password",
              })}
            </span>{" "}
            <span style={advertiserTitleStyle}>
              ELNYDO Ads
            </span>
          </h1>

          <p style={subtitleStyle}>
            {t("resetPassword.subtitle", {
              defaultValue:
                "Choose a new password for your advertiser account.",
            })}
          </p>

          {!token && (
            <div style={errorBox}>
              {t("resetPassword.invalidToken", {
                defaultValue:
                  "The password reset link is invalid or incomplete.",
              })}
            </div>
          )}

          <form
            onSubmit={handleResetPassword}
            style={formStyle}
          >
            <div style={fieldGroup}>
              <label
                htmlFor="adNewPassword"
                style={labelStyle}
              >
                {t("resetPassword.newPassword", {
                  defaultValue: "New password",
                })}
              </label>

              <div style={inputWrapper}>
                <input
                  id="adNewPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError("");
                    setSuccess("");
                  }}
                  autoComplete="new-password"
                  placeholder={t(
                    "resetPassword.placeholder",
                    {
                      defaultValue:
                        "At least 8 characters, including a number",
                    }
                  )}
                  style={inputStyle}
                  disabled={loading || !token}
                  required
                />

                <div style={inputStatusIcon}>
                  {newPassword.length > 0 &&
                    (passwordValid ? (
                      <span style={{ color: "#22c55e" }}>
                        ✓
                      </span>
                    ) : (
                      <span style={{ color: "#f87171" }}>
                        ✕
                      </span>
                    ))}
                </div>
              </div>

              {newPassword.length > 0 && (
                <div style={passwordChecksStyle}>
                  <span
                    style={{
                      color: passwordChecks.length
                        ? "#22c55e"
                        : "#f87171",
                    }}
                  >
                    {passwordChecks.length ? "✓" : "✕"}{" "}
                    {t("resetPassword.eightCharacters", {
                      defaultValue: "At least 8 characters",
                    })}
                  </span>

                  <span
                    style={{
                      color: passwordChecks.digit
                        ? "#22c55e"
                        : "#f87171",
                    }}
                  >
                    {passwordChecks.digit ? "✓" : "✕"}{" "}
                    {t("resetPassword.oneNumber", {
                      defaultValue: "At least one number",
                    })}
                  </span>
                </div>
              )}
            </div>

            <div style={fieldGroup}>
              <label
                htmlFor="adConfirmPassword"
                style={labelStyle}
              >
                {t(
                  "resetPassword.confirmNewPassword",
                  {
                    defaultValue:
                      "Confirm new password",
                  }
                )}
              </label>

              <div style={inputWrapper}>
                <input
                  id="adConfirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError("");
                    setSuccess("");
                  }}
                  autoComplete="new-password"
                  placeholder={t(
                    "resetPassword.confirmPlaceholder",
                    {
                      defaultValue:
                        "Repeat your new password",
                    }
                  )}
                  style={inputStyle}
                  disabled={loading || !token}
                  required
                />

                <div style={inputStatusIcon}>
                  {confirmPassword.length > 0 &&
                    (passwordsMatch ? (
                      <span style={{ color: "#22c55e" }}>
                        ✓
                      </span>
                    ) : (
                      <span style={{ color: "#f87171" }}>
                        ✕
                      </span>
                    ))}
                </div>
              </div>

              {confirmPassword.length > 0 && (
                <div style={matchStatusStyle}>
                  <span
                    style={{
                      color: passwordsMatch
                        ? "#22c55e"
                        : "#f87171",
                    }}
                  >
                    {passwordsMatch ? "✓" : "✕"}{" "}
                    {passwordsMatch
                      ? t("resetPassword.passwordsMatch", {
                          defaultValue:
                            "Passwords match",
                        })
                      : t(
                          "resetPassword.passwordsDoNotMatch",
                          {
                            defaultValue:
                              "Passwords do not match",
                          }
                        )}
                  </span>
                </div>
              )}
            </div>

            {error && (
              <div style={errorBox}>
                {error}
              </div>
            )}

            {success && (
              <div style={successBox}>
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              style={
                canSubmit
                  ? submitButton
                  : disabledSubmitButton
              }
            >
              {loading
                ? t("resetPassword.resetting", {
                    defaultValue: "Resetting…",
                  })
                : t("resetPassword.resetButton", {
                    defaultValue: "Reset password",
                  })}
            </button>
          </form>

          <div style={legalBlock}>
            <div style={{ marginBottom: 8 }}>
              {t("legalNoticeforgotpassword", {
                defaultValue:
                  "By continuing, you agree to our policies.",
              })}
            </div>

            <span
              onClick={() =>
                navigate("/legal/privacypolicy")
              }
              style={legalLink}
            >
              {t(
                "profileSetup.privacyPolicy",
                "Privacy Policy"
              )}
            </span>

            {" · "}

            <span
              onClick={() =>
                navigate("/legal/termsofuse")
              }
              style={legalLink}
            >
              {t(
                "profileSetup.termsOfUse",
                "Terms of Use"
              )}
            </span>
          </div>
        </section>
      </main>

      <StartFooter />
    </div>
  );
};

export default ResetPasswordAds;

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  width: "100%",
  background:
    "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#f9fafb",
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: "flex",
  flexDirection: "column",
};

const floatingLogoStyle: React.CSSProperties = {
  width: "100%",
  padding: "28px 20px 0",
  boxSizing: "border-box",
};

const floatingLogoButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "42px 32px",
  boxSizing: "border-box",
};

const resetPanel: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: 460,
  borderRadius: 28,
  border: "1px solid rgba(148,163,184,0.24)",
  background:
    "linear-gradient(145deg, rgba(15,23,42,0.94), rgba(2,6,23,0.98))",
  boxShadow: "0 30px 80px rgba(0,0,0,0.75)",
  padding: "38px 42px 40px",
  boxSizing: "border-box",
};

const backButton: React.CSSProperties = {
  position: "absolute",
  top: 4,
  left: -2,
  width: 42,
  height: 42,
  background: "transparent",
  border: "none",
  color: "#f59e0b",
  cursor: "pointer",
  fontSize: 30,
  lineHeight: "30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const titleStyle: React.CSSProperties = {
  margin: "10px 0 12px",
  fontSize: 31,
  lineHeight: 1.1,
  letterSpacing: "-0.8px",
  fontWeight: 800,
  textAlign: "center",
};

const advertiserTitleStyle: React.CSSProperties = {
  background:
    "linear-gradient(135deg, #f97316 0%, #facc15 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const subtitleStyle: React.CSSProperties = {
  margin: "0 auto 26px",
  color: "#94a3b8",
  fontSize: 13,
  lineHeight: 1.5,
  maxWidth: 340,
  textAlign: "center",
};

const formStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
};

const fieldGroup: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 7,
};

const labelStyle: React.CSSProperties = {
  marginLeft: 5,
  color: "#cbd5e1",
  fontSize: 12,
  fontWeight: 600,
};

const inputWrapper: React.CSSProperties = {
  position: "relative",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 50,
  padding: "0 44px 0 18px",
  borderRadius: 16,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(15,23,42,0.45)",
  color: "#f8fafc",
  fontSize: 15,
  outline: "none",
  boxSizing: "border-box",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.03)",
};

const inputStatusIcon: React.CSSProperties = {
  position: "absolute",
  right: 16,
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: 16,
  pointerEvents: "none",
};

const passwordChecksStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  marginTop: 2,
  paddingLeft: 5,
  fontSize: 11,
};

const matchStatusStyle: React.CSSProperties = {
  minHeight: 16,
  marginTop: 2,
  paddingLeft: 5,
  fontSize: 11,
};

const errorBox: React.CSSProperties = {
  padding: "11px 14px",
  borderRadius: 12,
  border: "1px solid rgba(248,113,113,0.3)",
  background: "rgba(127,29,29,0.22)",
  color: "#fca5a5",
  textAlign: "center",
  fontSize: 13,
  lineHeight: 1.45,
};

const successBox: React.CSSProperties = {
  padding: "11px 14px",
  borderRadius: 12,
  border: "1px solid rgba(34,197,94,0.3)",
  background: "rgba(20,83,45,0.24)",
  color: "#86efac",
  textAlign: "center",
  fontSize: 13,
  lineHeight: 1.45,
};

const submitButton: React.CSSProperties = {
  marginTop: 6,
  padding: "13px 18px",
  borderRadius: 999,
  border: "1px solid rgba(250,250,250,0.22)",
  background:
    "linear-gradient(135deg, rgba(212,175,55,0.98), rgba(250,250,210,0.92))",
  color: "#111827",
  fontWeight: 800,
  fontSize: 15,
  cursor: "pointer",
  boxShadow:
    "0 12px 30px rgba(212,175,55,0.16)",
};

const disabledSubmitButton: React.CSSProperties = {
  ...submitButton,
  background: "rgba(75,85,99,0.65)",
  border: "1px solid rgba(148,163,184,0.18)",
  color: "#cbd5e1",
  cursor: "not-allowed",
  boxShadow: "none",
};

const legalBlock: React.CSSProperties = {
  marginTop: 24,
  textAlign: "center",
  fontSize: 10.5,
  color: "#64748b",
  lineHeight: 1.5,
};

const legalLink: React.CSSProperties = {
  cursor: "pointer",
  color: "#94a3b8",
};